import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Pencil } from "lucide-react";
import TextInput from "./TextInput.jsx";

/**
 * EditableTitle — título editable in-situ.
 *
 * En reposo se ve como texto (con un lápiz que aparece al hover). Al hacer
 * click, Enter o F2 se convierte en un input con el texto seleccionado.
 * Enter y blur confirman; Escape descarta.
 *
 * `onCommit` puede devolver una promesa: mientras esté en vuelo se muestra el
 * estado de guardado y el input queda deshabilitado. Si la promesa rechaza, el
 * título vuelve al valor anterior (quien llama decide si avisa del error).
 */
function EditableTitle({
    value,
    onCommit,
    placeholder = "Sin título",
    maxLength = 60,
    disabled = false,
    className = "",
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? "");
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef(null);
    // El blur dispara el commit, pero Escape también quita el foco: sin esta
    // marca, cancelar guardaría igualmente.
    const cancelledRef = useRef(false);

    // El valor puede cambiar por fuera (carga del modelo, respuesta del PUT).
    useEffect(() => {
        if (!isEditing) setDraft(value ?? "");
    }, [value, isEditing]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const startEditing = () => {
        if (disabled || isSaving) return;
        cancelledRef.current = false;
        setDraft(value ?? "");
        setIsEditing(true);
    };

    const commit = async () => {
        if (cancelledRef.current) {
            cancelledRef.current = false;
            setIsEditing(false);
            return;
        }

        const next = draft.trim();
        setIsEditing(false);

        // Vacío o sin cambios: no se molesta al backend.
        if (!next || next === (value ?? "").trim()) {
            setDraft(value ?? "");
            return;
        }

        setIsSaving(true);
        try {
            await onCommit(next);
        } catch {
            setDraft(value ?? "");
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            commit();
        } else if (event.key === "Escape") {
            event.preventDefault();
            cancelledRef.current = true;
            setDraft(value ?? "");
            setIsEditing(false);
        }
    };

    if (isEditing) {
        return (
            <TextInput
                ref={inputRef}
                inputSize="sm"
                value={draft}
                maxLength={maxLength}
                placeholder={placeholder}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={commit}
                aria-label="Nombre del diseño"
                className={`max-w-[16rem] ${className}`}
            />
        );
    }

    return (
        <button
            type="button"
            onClick={startEditing}
            onKeyDown={(event) => {
                if (event.key === "F2") {
                    event.preventDefault();
                    startEditing();
                }
            }}
            disabled={disabled || isSaving}
            title="Renombrar diseño"
            aria-label={`Renombrar diseño: ${value || placeholder}`}
            className={`group flex max-w-[16rem] items-center gap-1.5 rounded-md px-2 py-1
                        text-sm font-medium transition-colors
                        hover:bg-base-content/10 focus-visible:outline focus-visible:outline-2
                        focus-visible:outline-offset-2 focus-visible:outline-primary
                        disabled:opacity-60 ${className}`}
        >
            <span className={`truncate ${value ? "" : "text-base-content/50 italic"}`}>
                {value || placeholder}
            </span>
            {isSaving ? (
                <span className="loading loading-spinner loading-xs shrink-0" />
            ) : (
                <Pencil className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
            )}
        </button>
    );
}

EditableTitle.propTypes = {
    value: PropTypes.string,
    onCommit: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    maxLength: PropTypes.number,
    disabled: PropTypes.bool,
    className: PropTypes.string,
};

export default EditableTitle;
